<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="800px"
    :before-close="handleClose"
    :close-on-click-modal="false"
    custom-class="payment-dialog"
  >
    <div slot="title" class="dialog-header">
      <h3>积分充值 · {{ selectedProduct.title }}</h3>
      <p>{{ selectedProduct.points }} 点数</p>
    </div>

    <div class="payment-content" v-loading="loading" element-loading-text="正在生成支付二维码...">
      <!-- 支付方式选择 -->
      <div class="payment-tabs">
        <div 
          class="payment-tab" 
          :class="{ active: paymentMethod === 'wechat' }"
          @click="selectPaymentMethod('wechat')"
        >
          <img src="@/assets/wechat.png" alt="微信支付" class="payment-icon-img" />
          <span>微信支付</span>
        </div>
        <div 
          class="payment-tab" 
          :class="{ active: paymentMethod === 'alipay' }"
          @click="selectPaymentMethod('alipay')"
        >
          <img src="@/assets/alipay.png" alt="支付宝支付" class="payment-icon-img" />
          <span>支付宝支付</span>
        </div>
      </div>

      <!-- 二维码区域 -->
      <div class="qrcode-area">
        <!-- 微信支付二维码 -->
        <div class="qrcode-box" v-if="codeUrl && paymentMethod === 'wechat'">
          <qriously :value="codeUrl" :size="240" />
          <div class="payment-icon">
            <img src="@/assets/wechat.png" alt="微信支付" class="payment-center-icon" />
          </div>
        </div>
        
        <!-- 支付宝支付二维码 (iframe嵌套) -->
        <div class="alipay-iframe-box" v-else-if="alipayUrl && paymentMethod === 'alipay'">
          <iframe 
            ref="alipayIframe"
            :src="alipayUrl" 
            frameborder="0"
            scrolling="no"
            class="alipay-iframe"
          ></iframe>
        </div>
        
        <!-- 加载占位符 -->
        <div class="qrcode-placeholder" v-else>
          <i class="el-icon-loading" v-if="loading"></i>
          <p v-if="!loading">请选择支付方式</p>
        </div>
      </div>

      <!-- 价格信息 -->
      <div class="price-section">
        <div class="price-details">
          <div class="price-item">
            <span class="price-item-label">充值金额：</span>
            <span class="price-item-value">¥{{ (selectedProduct.price / 100).toFixed(2) }}</span>
          </div>
          <div class="price-item fee-item">
            <span class="price-item-label">支付平台手续费（0.8%）：</span>
            <span class="price-item-value">¥{{ getFee() }}</span>
          </div>
          <div class="price-divider"></div>
          <div class="price-item total-item">
            <span class="price-item-label">实付金额：</span>
            <div class="total-price">
              <span class="price-label">¥</span>
              <span class="price-amount">{{ getTotalPrice() }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 支付提示 -->
      <div class="payment-tips">
        <p class="tip-main" v-if="paymentMethod === 'wechat'">请使用微信扫码支付</p>
        <p class="tip-main" v-else-if="paymentMethod === 'alipay'">请使用支付宝扫码支付</p>
        <p class="tip-sub">付款即同意，虚拟商品一经支付不支持退款</p>
      </div>

      <!-- 订单状态提示 -->
      <div class="order-status" v-if="orderNo">
        <p class="status-text">订单号: {{ orderNo }}</p>
        <p class="status-waiting" v-if="orderStatus === 'waiting'">
          <i class="el-icon-time"></i> 等待支付中，请使用{{ paymentMethod === 'wechat' ? '微信' : '支付宝' }}扫码
        </p>
        <p class="status-checking" v-else-if="orderStatus === 'processing'">
          <i class="el-icon-loading"></i> 支付处理中，请稍候...
        </p>
        <p class="status-checking" v-else-if="isCheckingPayment">
          <i class="el-icon-loading"></i> 正在检查支付状态...
        </p>
      </div>

      <!-- 取消订单按钮 -->
      <div class="cancel-section" v-if="orderNo">
        <el-button size="small" @click="cancelOrder">取消订单</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'PaymentMethodModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      paymentMethod: 'wechat', // wechat | alipay
      loading: false,
      codeUrl: '', // 微信支付二维码URL
      alipayUrl: '', // 支付宝支付页面URL（用于iframe）
      orderNo: '', // 订单号
      timer: null, // 定时器，用于轮询订单状态
      isCheckingPayment: false, // 是否正在检查支付状态
      orderStatus: '' // 订单状态：waiting-待支付, processing-支付中
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    selectedProduct() {
      return this.product || {}
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.selectedProduct.id) {
        // 打开弹窗时，根据当前支付方式自动生成支付二维码
        this.generatePaymentCode()
      } else if (!newVal) {
        // 关闭弹窗时，清理定时器和状态
        this.cleanupPayment()
      }
    }
  },
  methods: {
    // 计算手续费（0.8%）
    getFee() {
      const basePrice = this.selectedProduct.price / 100
      const fee = basePrice * 0.008
      return fee.toFixed(2)
    },

    // 计算总价（原价 + 0.8%手续费）
    getTotalPrice() {
      const basePrice = this.selectedProduct.price / 100
      const fee = basePrice * 0.008
      const total = basePrice + fee
      return total.toFixed(2)
    },

    // 选择支付方式
    selectPaymentMethod(method) {
      if (this.paymentMethod === method) {
        return
      }
      
      // 清理之前的支付状态
      this.cleanupPayment()
      
      this.paymentMethod = method
      
      // 切换支付方式时自动生成支付二维码/页面
      this.generatePaymentCode()
    },

    // 生成支付二维码
    async generatePaymentCode() {
      if (!this.selectedProduct.id) {
        this.$message.warning('请先选择商品')
        return
      }

      // 清理之前的定时器
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }

      this.loading = true
      this.codeUrl = ''
      this.alipayUrl = ''
      this.orderNo = ''

      try {
        if (this.paymentMethod === 'wechat') {
          // 调用微信支付接口
          const res = await this.request.post(`/wx-pay/native/${this.selectedProduct.id}`)
          
          if (res.code === '200' && res.data) {
            this.codeUrl = res.data.codeUrl
            this.orderNo = res.data.orderNo
            
            // 启动定时器，每3秒查询一次订单状态
            this.startOrderStatusCheck()
            
            this.$message.success('支付二维码已生成，请扫码支付')
          } else {
            this.$message.error(res.msg || '生成支付二维码失败')
          }
        } else if (this.paymentMethod === 'alipay') {
          // 调用支付宝支付接口
          const res = await this.request.post(`/ali-pay/trade/page/pay/${this.selectedProduct.id}`)
          
          if (res.code === '200' && res.data) {
            // 从返回的 HTML 中提取订单号 (out_trade_no)
            // 订单号在 biz_content 的 JSON 中，使用 &quot; 作为引号
            const orderNoMatch = res.data.match(/&quot;out_trade_no&quot;:&quot;([^&]+)&quot;/)
            console.log('orderNoMatch:', orderNoMatch)
            
            if (orderNoMatch && orderNoMatch[1]) {
              this.orderNo = orderNoMatch[1]
              console.log('支付宝订单号:', this.orderNo)
              
              // 启动定时器，轮询订单状态
              this.startOrderStatusCheck()
            } else {
              console.warn('无法从支付宝表单中提取订单号')
            }
            
            // 给支付宝返回的 HTML 添加居中样式
            const styledHtml = `
              <!DOCTYPE html>
              <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body {
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background: #fff;
                  }
                  form {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  }
                </style>
              </head>
              <body>
                ${res.data}
              </body>
              </html>
            `
            
            // 将 HTML 表单字符串转换为 Blob URL，用于 iframe src
            const blob = new Blob([styledHtml], { type: 'text/html;charset=utf-8' })
            this.alipayUrl = URL.createObjectURL(blob)
            
            this.$message.success('支付宝二维码已生成，请扫码支付')
          } else {
            this.$message.error(res.msg || '生成支付宝支付失败')
          }
        }
      } catch (error) {
        console.error('生成支付二维码失败:', error)
        this.$message.error('生成支付二维码失败，请重试')
      } finally {
        this.loading = false
      }
    },

    // 启动订单状态检查
    startOrderStatusCheck() {
      if (this.timer) {
        clearInterval(this.timer)
      }

      // 初始状态为等待支付
      this.orderStatus = 'waiting'
      this.isCheckingPayment = false

      this.timer = setInterval(async () => {
        await this.checkOrderStatus()
      }, 3000) // 每3秒查询一次
      
      // 延迟1秒后执行第一次查询，让用户先看到二维码
      setTimeout(() => {
        this.checkOrderStatus()
      }, 1000)
    },

    // 查询订单状态
    async checkOrderStatus() {
      if (!this.orderNo) {
        return
      }

      // 设置检查状态
      this.isCheckingPayment = true

      try {
        const res = await this.request.get(`/orderInfo/query-order-status/${this.orderNo}`)
        
        if (res.code === '200') {
          // 支付成功
          this.orderStatus = 'success'
          this.$message.success('支付成功！积分已到账')
          
          // 停止定时器
          this.cleanupPayment()
          
          // 关闭弹窗
          this.dialogVisible = false
          
          // 通知父组件刷新用户信息
          this.$emit('payment-success')
          
          // 刷新用户积分
          this.refreshUserPoints()
        } else if (res.code === '101') {
          // 待支付，继续轮询
          this.orderStatus = 'waiting'
          this.isCheckingPayment = false
          console.log('订单状态: 待支付')
        } else if (res.code === '102') {
          // 支付中，继续轮询
          this.orderStatus = 'processing'
          this.isCheckingPayment = false
          console.log('订单状态: 支付中')
        } else {
          // 其他错误状态（可能是订单关闭、取消等）
          console.log('订单状态异常:', res.msg)
          this.$message.warning(res.msg || '订单状态异常')
          this.cleanupPayment()
        }
      } catch (error) {
        console.error('查询订单状态失败:', error)
        this.isCheckingPayment = false
        // 网络错误不停止轮询，继续尝试
      }
    },

    // 取消订单
    async cancelOrder() {
      if (!this.orderNo) {
        return
      }

      try {
        const confirm = await this.$confirm('确定要取消订单吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).catch(() => false)

        if (!confirm) {
          return
        }

        this.loading = true
        
        // 根据支付方式调用不同的取消接口
        let res
        if (this.paymentMethod === 'wechat') {
          res = await this.request.post(`/wx-pay/cancel/${this.orderNo}`)
        } else if (this.paymentMethod === 'alipay') {
          res = await this.request.post(`/ali-pay/trade/close/${this.orderNo}`)
        }
        
        if (res && res.code === '200') {
          this.$message.success('订单已取消')
          this.cleanupPayment()
          this.dialogVisible = false
        } else {
          this.$message.error(res.msg || '取消订单失败')
        }
      } catch (error) {
        console.error('取消订单失败:', error)
        this.$message.error('取消订单失败，请重试')
      } finally {
        this.loading = false
      }
    },

    // 刷新用户积分
    async refreshUserPoints() {
      try {
        const user = JSON.parse(localStorage.getItem('user'))
        if (user && user.id) {
          const res = await this.request.get(`/user/${user.id}`)
          if (res.code === '200' && res.data) {
            // 更新本地存储的用户信息
            const updatedUser = { ...user, points: res.data.points }
            localStorage.setItem('user', JSON.stringify(updatedUser))
            
            // 通知 Vuex store 更新用户信息
            if (this.$store) {
              this.$store.commit('setUser', updatedUser)
            }
            
            // 触发全局积分刷新事件
            if (this.$refreshPoints) {
              this.$refreshPoints()
            }
          }
        }
      } catch (error) {
        console.error('刷新用户积分失败:', error)
      }
    },

    // 清理支付相关状态
    cleanupPayment() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      this.isCheckingPayment = false
      this.orderStatus = ''
      this.codeUrl = ''
      
      // 释放 Blob URL
      if (this.alipayUrl) {
        URL.revokeObjectURL(this.alipayUrl)
        this.alipayUrl = ''
      }
      
      this.orderNo = ''
    },

    // 关闭弹窗
    handleClose() {
      this.cleanupPayment()
      this.dialogVisible = false
    }
  },

  // 组件销毁时清理定时器
  beforeDestroy() {
    this.cleanupPayment()
  }
}
</script>

<style scoped>
/* 对话框标题 */
.dialog-header {
  text-align: center;
  padding: 4px 0;
}

.dialog-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 2px;
}

.dialog-header p {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* 支付内容 */
.payment-content {
  padding: 12px 0;
}

/* 支付方式标签 */
.payment-tabs {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
}

.payment-tab {
  flex: 1;
  max-width: 200px;
  padding: 12px 20px;
  border: 2px solid #e5e7eb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s;
}

.payment-tab:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.payment-tab.active {
  border-color: #6366f1;
  background: #f5f3ff;
  color: #6366f1;
}

.payment-tab i {
  font-size: 20px;
}

.payment-icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

/* 二维码区域 */
.qrcode-area {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.qrcode-box {
  position: relative;
  width: 280px;
  height: 280px;
  background: #fff;
  padding: 12px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-box canvas {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qrcode-placeholder {
  width: 280px;
  height: 280px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
}

.qrcode-placeholder i {
  font-size: 32px;
  margin-bottom: 12px;
}

.payment-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.95);
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.payment-center-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

/* 支付宝 iframe 区域 */
.alipay-iframe-box {
  position: relative;
  width: 280px;
  height: 280px;
  background: #fff;
  padding: 20px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.alipay-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* 价格区域 */
.price-section {
  margin-bottom: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-radius: 8px;
}

.price-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.price-item-label {
  color: #6b7280;
  font-weight: 400;
}

.price-item-value {
  color: #374151;
  font-weight: 500;
}

.fee-item .price-item-value {
  color: #f59e0b;
}

.price-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

.total-item {
  padding-top: 4px;
}

.total-item .price-item-label {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}

.total-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.price-label {
  font-size: 16px;
  font-weight: 600;
  color: #6366f1;
}

.price-amount {
  font-size: 24px;
  font-weight: 700;
  color: #6366f1;
  line-height: 1;
}

/* 支付提示 */
.payment-tips {
  text-align: center;
}

.tip-main {
  font-size: 14px;
  color: #374151;
  margin: 0 0 6px;
  font-weight: 500;
}

.tip-sub {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.6;
}

.link-text {
  color: #6366f1;
  cursor: pointer;
}

.link-text:hover {
  text-decoration: underline;
}

/* 订单状态 */
.order-status {
  text-align: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.status-text {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 6px;
}

.status-waiting {
  font-size: 12px;
  color: #f59e0b;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.status-checking {
  font-size: 12px;
  color: #6366f1;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* 取消订单区域 */
.cancel-section {
  text-align: center;
  margin-top: 12px;
}
</style>

<style>
/* 全局样式 */
.payment-dialog {
  border-radius: 0;
}

.payment-dialog .el-dialog__header {
  padding: 16px 24px 0;
  border-bottom: none;
}

.payment-dialog .el-dialog__body {
  padding: 0 24px 16px;
}
</style>
